import { getApiUrl } from '../../../config/api';
import { handleAPIError } from '../../../services/apiErrorHandler';

export interface RegenerateScriptPayload {
  projectId: string;
  id?: string; // Use 'id' to match backend entity
  userIdea: string;
  language?: string; // Add language support (en/fr)
  format: string;
  duration: string;
  audience: string;
  tone: string;
  genre: string;
  setting: string;
  characters?: string[];
  additionalOptions: {
    keyPoints?: string;
    callToAction?: string;
  };
}

export interface RegenerateScriptResponse {
  success: boolean;
  projectId: string;
  generatedScript: string;
  id?: string; // Use 'id' consistently
  message?: string;
}

export class ScriptRegenerateService {
  /**
   * Regenerate script with complete structured payload
   */
  static async regenerateScript(payload: RegenerateScriptPayload): Promise<RegenerateScriptResponse> {
    console.log('🔄 ScriptRegenerateService: Regenerating with payload:', payload);

    const response = await fetch(getApiUrl('/script-generator/generate'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      await handleAPIError(response);
    }

    const result = await response.json();
    return result;
  }

  /**
   * Build payload from ScriptGenerator answers
   */
  static buildFromAnswers(
    projectId: string,
    userIdea: string,
    answers: Record<string, any>,
    scriptId?: string,
    language?: string // Add language parameter
  ): RegenerateScriptPayload {
    const payload: RegenerateScriptPayload = {
      projectId,
      userIdea: userIdea.trim(),
      language, // Include language in payload
      format: answers.scriptType as string,
      duration: answers.duration as string,
      audience: answers.audience as string,
      tone: answers.tone as string,
      genre: answers.genre as string,
      setting: answers.setting as string,
      characters: answers.characters ? [answers.characters as string] : undefined,
      additionalOptions: {
        keyPoints: answers.keyPoints,
        callToAction: answers.callToAction,
      }
    };

    // Add scriptId if this is a regeneration - backend knows it's regeneration by presence of id
    if (scriptId) {
      payload.id = scriptId;
    }

    return payload;
  }

  /**
   * Build payload from ScriptData (for ScriptLayout)
   */
  static buildFromScriptData(script: any): RegenerateScriptPayload {
    return {
      projectId: script.projectId,
      id: script.id, // Backend knows it's regeneration by presence of id
      userIdea: script.userIdea || script.userSelections?.userIdea || '',
      // Parse attributes back to structured format
      format: script.attributes?.format || script.userSelections?.scriptType || 'Story/Narrative',
      duration: script.attributes?.duration || script.userSelections?.duration || '1-2 minutes',
      audience: script.attributes?.audience || script.userSelections?.audience || 'General',
      tone: script.attributes?.tone || script.userSelections?.tone || 'Neutral',
      genre: script.attributes?.genre || script.userSelections?.genre || 'Entertainment',
      setting: script.attributes?.setting || script.userSelections?.setting || 'Various',
      characters: script.attributes?.characters || script.userSelections?.characters ? 
        [script.attributes?.characters || script.userSelections?.characters] : undefined,
      additionalOptions: {
        keyPoints: script.attributes?.keyPoints || script.userSelections?.keyPoints,
        callToAction: script.attributes?.callToAction || script.userSelections?.callToAction,
      }
    };
  }

  /**
   * Extract script ID from API response
   */
  static extractScriptId(result: RegenerateScriptResponse): string | null {
    return result.id || null;
  }

  /**
   * Extract generated script from API response
   */
  static extractGeneratedScript(result: RegenerateScriptResponse): string | null {
    return result.generatedScript || null;
  }
}
